/**
 * Live Trade Execution & Order Management (Phase 4).
 *
 * Hosts the full Preview -> Confirm -> Place -> Live Status -> Result
 * workflow (spec Section 27) on top of the SAME Trade Builder legs Sections
 * 1-6 above already hold — reusing `strategyAdjustmentService`'s and
 * `brokerValidationService`'s already-computed results is intentionally
 * NOT done here again: `executionService.preview()` itself re-derives
 * Phase 1/2/3's numbers server-side and freezes them into a signed token,
 * so this component only ever displays what the backend already computed
 * — see `app.services.execution.execution_service.ExecutionService`'s own
 * module docstring for the "never recalculate here" guarantee this mirrors.
 *
 * EXECUTION SAFETY:
 *   - No broker is ever auto-selected (Section 23) — the trader must pick
 *     one CONNECTED account from the dropdown before "Review Order" is
 *     even enabled.
 *   - "Review Order" only calls `POST /execution/{symbol}/preview` — a
 *     read-only preview; nothing is confirmed or placed yet.
 *   - The mandatory checkbox + [Confirm & Place Order] button is the ONE
 *     final confirmation in the whole Strategy Builder -> Execution flow
 *     (the page-level "User Confirmation" card and the separate,
 *     display-only "Execution Preview" card that used to sit above this
 *     panel were both removed as redundant/contradictory — see
 *     `StrategyAdjustmentPage.tsx`'s own docstring) — and it is the ONLY
 *     control that can lead to a real broker order, and even then only
 *     after the backend's own `TRADING_MODE=LIVE` gate, idempotency check,
 *     and a FRESH final broker validation all pass — this component cannot
 *     bypass any of those; a failed gate just shows the backend's own
 *     "Execution blocked: ..." message.
 *   - Once orders are placed, this component only ever POLLS status or
 *     lets the trader cancel a still-open order — it never automatically
 *     retries, hedges, rolls, or resubmits anything (spec Section 32).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import brokerAccountService from "@/services/brokerAccountService";
import executionService from "@/services/executionService";
import type {
  BrokerAccountRead,
  BrokerName,
  BrokerOrderRead,
  ConfirmExecutionRead,
  ExecutionResultRead,
  ExecutionStatusRead,
  LiveExecutionPreviewRead,
  OptionLeg,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { BROKER_DISPLAY_LABELS, fmtCurrency } from "@/pages/strategy-adjustment/constants";
import {
  ORDER_STATE_COLORS,
  ORDER_STATE_LABELS,
  STATUS_POLL_INTERVAL_MS,
  STRATEGY_STATUS_COLORS,
  STRATEGY_STATUS_LABELS,
  TERMINAL_STRATEGY_STATUSES,
} from "./constants";

// ANGEL_ONE added 2026-10-23 (orders resolved from the Angel One scrip master; server switch ANGEL_ONE_ORDER_EXECUTION_ENABLED).
const ALL_BROKERS: BrokerName[] = ["DHAN", "ZERODHA", "ICICI_BREEZE", "ANGEL_ONE"];

function OrdersTable({
  orders,
  onCancel,
}: {
  orders: BrokerOrderRead[];
  onCancel: (order: BrokerOrderRead) => void;
}) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Leg</TableCell>
          <TableCell>Broker Order ID</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right">Filled Qty</TableCell>
          <TableCell align="right">Avg. Price</TableCell>
          <TableCell>Message</TableCell>
          <TableCell align="right">Action</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {orders.map((order) => (
          <TableRow key={order.execution_leg_id}>
            <TableCell>
              {order.side} {order.strike}
              {order.option_type} × {order.quantity}
            </TableCell>
            <TableCell>{order.broker_order_id ?? "—"}</TableCell>
            <TableCell>
              <Chip size="small" label={ORDER_STATE_LABELS[order.status]} color={ORDER_STATE_COLORS[order.status]} />
            </TableCell>
            <TableCell align="right">{order.filled_quantity}</TableCell>
            <TableCell align="right">{order.average_price != null ? fmtCurrency(order.average_price) : "—"}</TableCell>
            <TableCell>
              <Typography variant="caption" color="text.secondary">
                {order.message ?? "—"}
              </Typography>
            </TableCell>
            <TableCell align="right">
              {order.cancellable && (
                <Button size="small" color="warning" onClick={() => onCancel(order)}>
                  Cancel
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function ExecutionPanel({
  symbol,
  originalStrategyKey,
  legs,
}: {
  symbol: string;
  originalStrategyKey: string;
  legs: OptionLeg[];
}) {
  const [accounts, setAccounts] = useState<BrokerAccountRead[]>([]);
  const [selectedBroker, setSelectedBroker] = useState<BrokerName | "">("");

  const [preview, setPreview] = useState<LiveExecutionPreviewRead | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const [acknowledged, setAcknowledged] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [placeError, setPlaceError] = useState<string | null>(null);
  const [execution, setExecution] = useState<ConfirmExecutionRead | null>(null);
  const [status, setStatus] = useState<ExecutionStatusRead | null>(null);
  const [result, setResult] = useState<ExecutionResultRead | null>(null);

  const [cancelTarget, setCancelTarget] = useState<BrokerOrderRead | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);

  const pollRef = useRef<ReturnType<typeof globalThis.setInterval> | null>(null);

  useEffect(() => {
    brokerAccountService
      .list()
      .then((res) => setAccounts(res.items.filter((a) => a.is_connected)))
      .catch(() => setAccounts([]));
  }, []);

  // Countdown tick for the preview's Valid Until deadline (Section 4).
  useEffect(() => {
    if (!preview) return;
    const id = globalThis.setInterval(() => setNow(Date.now()), 1000);
    return () => globalThis.clearInterval(id);
  }, [preview]);

  const previewExpired = preview ? now > new Date(preview.valid_until).getTime() : false;

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      globalThis.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Section 15 — controlled polling while the strategy is still in flight,
  // stopped as soon as it reaches a terminal state (never excessive, never
  // creates a duplicate order — this only ever GETs status).
  useEffect(() => {
    if (!execution || !status) return;
    if (TERMINAL_STRATEGY_STATUSES.includes(status.status)) {
      stopPolling();
      executionService.getResult(execution.execution_id).then(setResult).catch(() => undefined);
      return;
    }
    stopPolling();
    pollRef.current = globalThis.setInterval(() => {
      executionService
        .getStatus(execution.execution_id)
        .then(setStatus)
        .catch(() => undefined);
    }, STATUS_POLL_INTERVAL_MS);
    return stopPolling;
  }, [execution, status, stopPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const resetForNewLegs = useCallback(() => {
    setPreview(null);
    setPreviewError(null);
    setAcknowledged(false);
    setExecution(null);
    setStatus(null);
    setResult(null);
    setPlaceError(null);
    stopPolling();
  }, [stopPolling]);

  // If the trader goes back and edits the Trade Builder after previewing,
  // the old preview/confirmation no longer describes these legs — clear
  // everything downstream rather than let a stale preview look current.
  useEffect(() => {
    resetForNewLegs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legs, symbol]);

  const handleReviewOrder = async () => {
    if (!selectedBroker || legs.length === 0) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await executionService.preview(symbol, {
        original_strategy_key: originalStrategyKey,
        legs,
        broker_name: selectedBroker,
        product_type: "NRML",
      });
      setPreview(result);
      setAcknowledged(false);
    } catch (err) {
      setPreviewError(extractErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmAndPlace = async () => {
    if (!preview) return;
    setPlacing(true);
    setPlaceError(null);
    try {
      const confirmed = await executionService.confirm({
        preview_token: preview.preview_token,
        legs,
        acknowledged: true,
      });
      setExecution(confirmed);
      const placed = await executionService.place({
        execution_id: confirmed.execution_id,
        idempotency_key: confirmed.idempotency_key,
      });
      setStatus(placed);
    } catch (err) {
      setPlaceError(extractErrorMessage(err));
    } finally {
      setPlacing(false);
    }
  };

  const handleCancelConfirmed = async () => {
    if (!execution || !cancelTarget) return;
    setCancelBusy(true);
    try {
      await executionService.cancelOrder(execution.execution_id, { execution_leg_id: cancelTarget.execution_leg_id });
      const refreshed = await executionService.getStatus(execution.execution_id);
      setStatus(refreshed);
    } catch (err) {
      setPlaceError(extractErrorMessage(err));
    } finally {
      setCancelBusy(false);
      setCancelTarget(null);
    }
  };

  const alreadySubmitted = Boolean(execution);

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }} display="block" gutterBottom>
          Live Trade Execution — Preview, Confirm & Place Orders
        </Typography>

        {!execution && (
          <>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 260 }} disabled={previewLoading}>
                <InputLabel id="execution-broker-select-label">Broker Account (required)</InputLabel>
                <Select
                  labelId="execution-broker-select-label"
                  label="Broker Account (required)"
                  value={selectedBroker}
                  onChange={(e) => setSelectedBroker(e.target.value as BrokerName | "")}
                >
                  <MenuItem value="">
                    <em>Select a broker — never auto-chosen</em>
                  </MenuItem>
                  {ALL_BROKERS.map((broker) => {
                    const account = accounts.find((a) => a.broker_name === broker);
                    return (
                      <MenuItem key={broker} value={broker} disabled={!account}>
                        {BROKER_DISPLAY_LABELS[broker]}
                        {account ? ` ****${(account.client_id ?? "").slice(-4)} — Connected ✓` : " — Not connected"}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                startIcon={previewLoading ? <CircularProgress size={16} color="inherit" /> : <RocketLaunchIcon />}
                disabled={!selectedBroker || legs.length === 0 || previewLoading}
                onClick={handleReviewOrder}
              >
                {preview ? "Refresh Preview" : "Review Order"}
              </Button>
            </Stack>

            {previewError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {previewError}
              </Alert>
            )}

            {preview && (
              <>
                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Execution Preview — {preview.broker_account_label}
                  </Typography>
                  <Chip
                    size="small"
                    label={previewExpired ? "PREVIEW EXPIRED" : "PREVIEW VALID"}
                    color={previewExpired ? "error" : "success"}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Generated {new Date(preview.generated_at).toLocaleTimeString()} · Valid until{" "}
                  {new Date(preview.valid_until).toLocaleTimeString()}
                </Typography>

                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Leg</TableCell>
                      <TableCell>Broker Symbol</TableCell>
                      <TableCell>Exchange</TableCell>
                      <TableCell>Order Type</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.legs.map((leg) => (
                      <TableRow key={leg.leg_id}>
                        <TableCell>
                          {leg.side} {leg.strike}
                          {leg.option_type}
                        </TableCell>
                        <TableCell>{leg.broker_symbol ?? "—"}</TableCell>
                        <TableCell>{leg.exchange ?? "—"}</TableCell>
                        <TableCell>{leg.order_type}</TableCell>
                        <TableCell align="right">
                          {leg.quantity} × {leg.lot_size}
                        </TableCell>
                        <TableCell align="right">{leg.price != null ? fmtCurrency(leg.price) : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Net Premium (Phase 2, approved): {fmtCurrency(preview.risk_summary.net_premium)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Estimated Margin: {fmtCurrency(preview.risk_summary.estimated_margin)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Broker Readiness: {preview.broker_validation.strategy_status}
                  </Typography>
                </Stack>

                {previewExpired ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This preview has expired. Click "Refresh Preview" above to revalidate before confirming.
                  </Alert>
                ) : (
                  <>
                    <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
                      LIVE TRADING — Orders will be sent to your broker account. A final broker validation runs
                      immediately before placement; if it fails, no order is sent.
                    </Alert>

                    <FormControlLabel
                      control={<Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />}
                      label="I confirm that I have reviewed the strategy, quantities, prices and estimated risk."
                      sx={{ mb: 1.5, display: "block" }}
                    />

                    {placeError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {placeError}
                      </Alert>
                    )}

                    <Button
                      variant="contained"
                      color="error"
                      size="large"
                      fullWidth
                      disabled={!acknowledged || placing || alreadySubmitted}
                      onClick={handleConfirmAndPlace}
                    >
                      {placing ? "Placing order…" : "Confirm & Place Order"}
                    </Button>
                  </>
                )}
              </>
            )}
          </>
        )}

        {execution && status && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Live Execution — {BROKER_DISPLAY_LABELS[status.broker]}
              </Typography>
              <Chip size="small" label={STRATEGY_STATUS_LABELS[status.status]} color={STRATEGY_STATUS_COLORS[status.status]} />
              {!TERMINAL_STRATEGY_STATUSES.includes(status.status) && <CircularProgress size={16} />}
            </Stack>

            {status.status === "REQUIRES_ATTENTION" && (
              <Alert severity="error" sx={{ mb: 2 }}>
                REQUIRES ATTENTION — not every leg filled. Review each leg below; this app never automatically
                resubmits, hedges, or repairs a partial execution. Any correction is a deliberate action you take
                yourself, starting a new trade.
              </Alert>
            )}
            {status.status === "PARTIALLY_EXECUTED" && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Some legs have filled while others are still in flight — this is not yet a complete execution.
              </Alert>
            )}
            {status.errors.map((e, i) => (
              <Alert severity="error" key={`err-${i}`} sx={{ mb: 1 }}>
                {e}
              </Alert>
            ))}
            {status.warnings.map((w, i) => (
              <Alert severity="warning" key={`warn-${i}`} sx={{ mb: 1 }}>
                {w}
              </Alert>
            ))}

            <OrdersTable orders={status.orders} onCancel={setCancelTarget} />

            {result && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Execution Result — Expected vs. Actual
                </Typography>
                <List dense disablePadding>
                  <ListItem disableGutters>
                    <ListItemText
                      primary={`Expected Net Premium: ${fmtCurrency(result.expected_vs_actual.expected_net_premium)}`}
                      secondary={`Actual Net Premium: ${fmtCurrency(result.expected_vs_actual.actual_net_premium)} (Slippage: ${fmtCurrency(
                        result.expected_vs_actual.net_premium_slippage
                      )})`}
                    />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemText
                      primary={`Orders Filled: ${result.orders_filled} / ${result.orders_total}`}
                      secondary={`Broker Order IDs: ${result.broker_order_ids.join(", ") || "—"}`}
                    />
                  </ListItem>
                  {result.execution_time && (
                    <ListItem disableGutters>
                      <ListItemText primary={`Execution Time: ${new Date(result.execution_time).toLocaleString()}`} />
                    </ListItem>
                  )}
                </List>
              </>
            )}
          </>
        )}

        {legs.length === 0 && !execution && (
          <Typography variant="body2" color="text.secondary">
            Add at least one leg above to review and execute an order.
          </Typography>
        )}
      </CardContent>

      <Dialog open={Boolean(cancelTarget)} onClose={() => setCancelTarget(null)}>
        <DialogTitle>Cancel this pending order?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cancelTarget && (
              <>
                {cancelTarget.side} {cancelTarget.strike}
                {cancelTarget.option_type} — broker order {cancelTarget.broker_order_id ?? "—"} will be cancelled.
                Already-filled orders can never be cancelled.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelBusy}>
            Keep Order
          </Button>
          <Button color="warning" onClick={handleCancelConfirmed} disabled={cancelBusy}>
            {cancelBusy ? "Cancelling…" : "Cancel Order"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default ExecutionPanel;
